import React, { useEffect, useMemo, useState } from "react";
import * as Utils from "../utils";
import { api, ApiError } from "../api/client";
import { useMessage } from "../components/MessageProvider";

const LockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 15v2m-4 4h8a2 2 0 002-2v-5a2 2 0 00-2-2H8a2 2 0 00-2 2v5a2 2 0 002 2zm8-9V7a4 4 0 10-8 0v5h8z"
    />
  </svg>
);

const BellIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.4-2.8A5 5 0 0118 11V9a6 6 0 10-12 0v2c0 1.1-.3 2.2-.9 3.2L3 17h5m7 0a3 3 0 11-6 0m6 0H9"
    />
  </svg>
);

function roundHours(value) {
  return Math.round(value * 10) / 10;
}

function normalizeHours(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatResiduo(base, previsto) {
  const baseNum = normalizeHours(base);
  const previstoNum = normalizeHours(previsto);
  const baseDisplay = baseNum !== null ? roundHours(baseNum) : "-";
  if (previstoNum === null) {
    return baseDisplay;
  }
  const totale = roundHours((baseNum ?? 0) + previstoNum);
  return `${baseDisplay} maturate - (${totale} a fine anno)`;
}

function InfoUtente({ user, isOnline, setUser, onMenuClick, onForcePushRegistration, onSetTopBarAction }) {
  const [residui, setResidui] = useState(user.residui);
  // Stato per cambio password
  const [showChangePsw, setShowChangePsw] = useState(false);
  const [newPsw, setNewPsw] = useState("");
  const [confirmPsw, setConfirmPsw] = useState("");
  const [pswError, setPswError] = useState("");
  const [pswSuccess, setPswSuccess] = useState("");
  const [loadingPsw, setLoadingPsw] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [deviceRegistered, setDeviceRegistered] = useState(Boolean(user?.token_notifiche_reg));
  const { showMessage } = useMessage();

  useEffect(() => {
    if (!onSetTopBarAction) return;
    onSetTopBarAction({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
        </svg>
      ),
      label: "Info Profilo",
      onClick: () => showMessage({
        title: "Profilo Utente",
        message: "Qui trovi i tuoi dati personali, i residui di ferie e permessi aggiornati e le impostazioni del tuo account. Da questa sezione puoi anche cambiare la password e registrare questo dispositivo per ricevere le notifiche push.",
        type: "info",
        timeout: 0,
      }),
    });
    return () => onSetTopBarAction(null);
  }, [onSetTopBarAction, showMessage]);

  const backendHasAnyToken = useMemo(() => {
    if (typeof user?.token_notifiche_has_any === "boolean") {
      return user.token_notifiche_has_any;
    }
    if (!user?.token_notifiche) return false;
    return user.token_notifiche
      .split(";")
      .map((token) => token.trim())
      .filter(Boolean).length > 0;
  }, [user?.token_notifiche_has_any, user?.token_notifiche]);

  useEffect(() => {
    async function update() {
      if (isOnline) {
        const userAggiornato = await Utils.aggiornaResidui(user);
        setResidui(userAggiornato.residui);
        setUser(userAggiornato);
        localStorage.setItem("easyjob_user", JSON.stringify({ ...userAggiornato, password: user.password }));
        await Utils.saveUserOffline({ ...userAggiornato, password: user.password, username: user.username });
      }
    }
    update();
    // eslint-disable-next-line
  }, []); // <-- togli 'user' e 'isOnline' dalle dipendenze

  // Validazione password
  function validatePassword(psw) {
    if (psw.length < 8) return "La password deve essere di almeno 8 caratteri.";
    if (!/[A-Z]/.test(psw)) return "La password deve contenere almeno una lettera maiuscola.";
    if (!/[0-9]/.test(psw)) return "La password deve contenere almeno un numero.";
    return "";
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPswError("");
    setPswSuccess("");
    const err = validatePassword(newPsw);
    if (err) {
      setPswError(err);
      return;
    }
    if (newPsw !== confirmPsw) {
      setPswError("Le password non coincidono.");
      return;
    }
    setLoadingPsw(true);
    try {
      const data = await api.post("/api/cambio_password", { session_token: user.token, new_psw: newPsw });
      if (data?.success) {
        setPswSuccess("Password cambiata con successo.");
        setShowChangePsw(false);
        setNewPsw("");
        setConfirmPsw("");
      } else {
        setPswError(data?.message || "Errore nel cambio password.");
      }
    } catch (err) {
      if (err instanceof ApiError && !err.isNetworkError) {
        setPswError(err.body?.message || err.message || "Errore nel cambio password.");
      } else {
        setPswError("Errore di connessione al server.");
      }
    }
    setLoadingPsw(false);
  }

  async function handleForceNotifications() {
    if (!onForcePushRegistration) return;
    setPushLoading(true);
    try {
      const result = await onForcePushRegistration();
      await showMessage({
        title: "Notifiche",
        message: result?.message || "Dispositivo registrato per le notifiche.",
        type: "success",
        timeout: 3,
      });
      setDeviceRegistered(true);
    } catch (err) {
      await showMessage({
        title: "Notifiche",
        message: err?.message || "Errore durante la registrazione del dispositivo.",
        type: "error",
      });
    } finally {
      setPushLoading(false);
    }
  }

  useEffect(() => {
    let active = true;
    async function checkDeviceRegistration() {
      if (
        !user?.id ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      ) {
        if (active) setDeviceRegistered(Boolean(user?.token_notifiche_reg));
        return;
      }
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          if (active) setDeviceRegistered(false);
          return;
        }
        const serialized = JSON.stringify(subscription);
        const matches = user?.token_notifiche_local
          ? serialized === user.token_notifiche_local
          : Boolean(user?.token_notifiche_reg);
        if (active) {
          setDeviceRegistered(matches);
        }
      } catch (err) {
        if (active) setDeviceRegistered(Boolean(user?.token_notifiche_reg));
      }
    }
    checkDeviceRegistration();
    return () => {
      active = false;
    };
  }, [user?.id, user?.token_notifiche_local, user?.token_notifiche_reg]);

  const showOtherDeviceNotice = backendHasAnyToken && !deviceRegistered;
  if (!user?.assunzione_id) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-4 max-w-md mx-auto mt-4 w-full text-center text-red-600">
        Nessun Dipendente associato a questo utente.
      </div>
    );
  }

  // Render updated residui structure
  const actionButtonBase =
    "w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2";

  return (
    <div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="bg-white rounded-xl shadow-lg p-4 max-w-md mx-auto mt-4 w-full">
        <div className="mb-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a8.25 8.25 0 0115 0"
              />
            </svg>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Scheda utente</p>
            <p className="text-lg font-semibold text-gray-900">{user.nome} {user.cognome}</p>
          </div>
        </div>
        <div className="mb-2"><strong>Email:</strong> {user.email}</div>
        <div className="mb-2"><strong>AssunzioneID:</strong> {user.assunzione_id}</div>
        <div className="mb-2"><strong>Matricola:</strong> {user?.matricola || "Nessun dipendente associato."}</div>
        <div className="mb-2">
          <strong>Residui (ore):</strong>
          {residui ? (
            <div className="mt-2 rounded-xl border border-gray-100 bg-gray-50 p-4 shadow-inner">
              <ul className="space-y-2 text-sm">
                <li>
                  <span className="font-semibold italic">Ferie:</span>
                  <span className="ml-2">{formatResiduo(residui.ferie, residui.previsto_ferie)}</span>
                </li>
                <li>
                  <span className="font-semibold italic">Permessi:</span>
                  <span className="ml-2">{formatResiduo(residui.permessi, residui.previsto_permessi)}</span>
                </li>
                <li>
                  <span className="font-semibold italic">Ex Festività:</span>
                  <span className="ml-2">{formatResiduo(residui.ex_festivita, residui.previsto_ex_festivita)}</span>
                </li>
                {/*
                <li>
                  <span className="font-semibold italic">Banca Ore:</span>
                  <span className="ml-2">{String(residui.banca_ore).replace(/<\/?br\s*\/?\>?/gi, "")}</span>
                </li>
                */}
                <li>
                  <span className="font-semibold italic">Mesi da maturare:</span>
                  <span className="ml-2">{residui.mesi_da_maturare ?? "-"}</span>
                </li>
                <li>
                  <span className="font-semibold italic">Percentuale maturata:</span>
                  <span className="ml-2">
                    {typeof residui.perc_maturata === "number"
                      ? `${residui.perc_maturata.toFixed(1)}%`
                      : residui.perc_maturata ?? "-"}
                  </span>
                </li>
                <li>
                  <span className="font-semibold italic">Anzianità:</span>
                  <span className="ml-2">{residui.anzianita ?? "-"}</span>
                </li>
                <li>
                  <span className="font-semibold italic">Regola maturazione ID:</span>
                  <span className="ml-2">{residui.regolaMaturazioneId ?? "-"}</span>
                </li>
              </ul>
            </div>
          ) : (
            <div className="text-gray-500">Residui non disponibili.</div>
          )}
        </div>
        {residui?.aggiornato_al && (
          <div className="mt-2 text-xs text-gray-500 text-right">
            Dati aggiornati al: {Utils.formatItalianDate(residui.aggiornato_al, true)}
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            className={`${actionButtonBase} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-400`}
            onClick={() => setShowChangePsw((v) => !v)}
          >
            <LockIcon />
            <span>{showChangePsw ? "Annulla" : "Cambia Password"}</span>
          </button>
          <button
            className={`${actionButtonBase} bg-green-500 text-white hover:bg-green-600 disabled:opacity-60 focus:ring-green-300`}
            onClick={handleForceNotifications}
            disabled={pushLoading || !isOnline}
          >
            <BellIcon />
            <span>{pushLoading ? "Registrazione..." : "Ricevi Notifiche Qui"}</span>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Richiede connessione attiva e permesso di notifica del browser.
        </p>
        {showOtherDeviceNotice && (
          <p className="text-xs text-blue-600 mt-1 text-center font-semibold">
            Dispositivo NON configurato per le notifiche.
          </p>
        )}
        {showChangePsw && (
          <form className="mt-4 p-4 border rounded bg-gray-50" onSubmit={handleChangePassword}>
            <div className="mb-2">
              <label className="block mb-1 font-semibold">Nuova Password</label>
              <input
                type="password"
                className="w-full border px-2 py-1 rounded"
                value={newPsw}
                onChange={e => setNewPsw(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="mb-2">
              <label className="block mb-1 font-semibold">Conferma Password</label>
              <input
                type="password"
                className="w-full border px-2 py-1 rounded"
                value={confirmPsw}
                onChange={e => setConfirmPsw(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            {pswError && <div className="text-red-600 mb-2">{pswError}</div>}
            {pswSuccess && <div className="text-green-600 mb-2">{pswSuccess}</div>}
            <button
              type="submit"
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded"
              disabled={loadingPsw}
            >
              {loadingPsw ? "Attendere..." : "Conferma Cambio Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default InfoUtente;